<template>
    <div class="view-information-corporate">
        <!-- Content + completion action share ONE measure container so their right
             edges always line up (Figma 17111:47009) and the action stays pinned to
             the bottom of the filled area regardless of body length. -->
        <div class="vi-corporate-measure vi-corporate-fill">
            <display-content class="vi-corporate-content" :content="activityContent.content" />

            <activity-completion-action-row class="vi-corporate-action">
                <e-button
                    v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                    :loading="isLoading"
                    :disabled="disabled"
                    type="is-primary"
                    @click="submit"
                >
                    {{ translate('mark_complete') }}
                </e-button>
            </activity-completion-action-row>
        </div>
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import { useActivity } from '@/composables/useActivity.js';

/**
 * Corporate presentation of the View Information activity.
 *
 * Behaviour is 100% shared with the stanbridge `ViewInformation.vue` via the
 * `useActivity` composable — this component differs ONLY in markup/styling
 * (corporate Figma: constrained readable measure + justified body). The
 * dispatcher (CourseActivityTypes) selects this variant for the corporate
 * client via CORPORATE_RENDERER_OVERRIDES; stanbridge keeps ViewInformation.vue.
 */
export default {
    name: 'ViewInformationCorporate',

    components: {
        ActivityCompletionActionRow,
        DisplayContent,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    computed: {
        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: [],
            };
        },
    },
};
</script>

<style scoped lang="scss">
.view-information-corporate {
    // The corporate shell section owns the 860px content measure; this class fills it
    // and binds the content + action + submissions to that one width so their right
    // edges line up. It's also the flex column that fills the content area, so the
    // action anchors to the BOTTOM (the shell chain provides no height — anchor to the
    // viewport minus the chrome: top bar + breadcrumb/title + footer + spacing).
    .vi-corporate-measure {
        max-width: 100%;
    }

    // Only the primary block fills the content area so the action can anchor to the
    // bottom; the submissions block (instructor view) just shares the measure width.
    .vi-corporate-fill {
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 22.5rem);
    }

    // Push the completion action to the bottom; it right-aligns within the measure,
    // so the button's right edge matches the content's right edge.
    .vi-corporate-action {
        margin-top: auto;
    }

    .vi-corporate-content :deep(p) {
        color: #363636; // Figma Primary/Elements/$text-strong
        font-size: 1rem; // 16px
        line-height: 1.5;
        text-align: justify;
    }
}
</style>
